import { useEffect } from "react";
import { useSearch } from "wouter";
import * as Sentry from "@sentry/react";
import type { RegionPageState, RegionRefusalReason } from "@/lib/region";

// WI#43/#44 — the web app launches in the United States and Canada only. Copy is Emily's (launch
// copy d00806b4). Three states (Kayla J4): confirmed outside the region is a final answer with a
// way to reach support; a location we couldn't confirm (lookup_failed) gets VPN guidance and Try
// again; the check itself failing (check_failed) is our outage, so it says so and offers Try again
// — never VPN advice. No waitlist: it closed at launch (#44).
const SUPPORT_MAILTO = "mailto:support@peakrunning.com";

export function RegionUnavailable({
  reason = "outside_region",
  onRetry,
  error,
}: {
  reason?: RegionPageState;
  onRetry?: () => void;
  error?: unknown;
}) {
  // Our failure must be visible, not just shown to the runner: one warning per time this state is
  // reached, one stable Sentry issue (fingerprint) however many runners hit it. origin_untrusted
  // shares the wording but not this event: the API answered, and reports that case itself (#84).
  useEffect(() => {
    if (reason !== "check_failed") return;
    const status = (error as { status?: number } | null | undefined)?.status;
    Sentry.captureMessage("Region check failed on sign-up — GET /api/region did not answer", {
      level: "warning",
      tags: { flow: "sign-up-region-check", status: status === undefined ? "network" : String(status) },
      fingerprint: ["region-check-failed"],
    });
  }, [reason, error]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-[440px] rounded border border-border bg-white p-8 shadow-md">
        <img src={`${import.meta.env.BASE_URL}logo-stacked.svg`} alt="PeakRunning" className="mx-auto mb-6 h-20 w-auto" />
        {reason === "outside_region" ? (
          <>
            <h1 className="font-display text-xl font-bold mb-2 text-center">PeakRunning isn’t available in your region yet</h1>
            <p className="text-muted-foreground text-sm mb-6 text-center">
              PeakRunning is currently available in the United States and Canada.
            </p>
            <p className="text-center text-sm">
              <a href={SUPPORT_MAILTO} className="underline font-semibold">
                Contact support
              </a>
            </p>
          </>
        ) : (
          <>
            <p role="alert" className="text-sm mb-6 text-center">
              {reason === "check_failed" || reason === "origin_untrusted"
                ? "We couldn’t check your location right now. Try again in a moment."
                : "We couldn’t confirm your location. If you’re in the United States or Canada, turn off any VPN and try again."}
            </p>
            <button
              type="button"
              onClick={onRetry}
              className="w-full bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 transition-opacity mb-3"
            >
              Try again
            </button>
            <p className="text-center text-sm">
              <a href={SUPPORT_MAILTO} className="underline">
                Contact support
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// /region — reached after the server refuses a first action (join, claim, solo check-in). Try
// again returns to the form the person came from, so they can repeat it after turning off a VPN.
export function RegionPage() {
  const param = new URLSearchParams(useSearch()).get("reason");
  const reason: RegionRefusalReason =
    param === "lookup_failed" || param === "origin_untrusted" ? param : "outside_region";
  const retry = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.assign(`${import.meta.env.BASE_URL.replace(/\/$/, "")}/`);
  };
  return <RegionUnavailable reason={reason} onRetry={retry} />;
}

export default RegionPage;
